import type { JsonObject } from "@/types/json"

import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import { isRecord, readJsonObject, readString } from "./actionArgumentReaders"

/** Keep the server-validated configuration intact while narrowing the signed proposal. */
export function parseAlertActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  if (action.kind === "create_alert" || action.kind === "update_alert") {
    const config = readJsonObject(action.args, "config")
    if (config && Object.values(config).some((value) => value === null)) {
      return {
        ok: false,
        problems: ["null alert fields are unsupported; use [] to clear lists"],
      }
    }
  }
  if (action.kind === "create_alert") {
    const config = readJsonObject(action.args, "config")
    if (
      !config ||
      typeof config.name !== "string" ||
      !config.name.trim() ||
      !Array.isArray(config.conditions) ||
      config.conditions.length === 0
    ) {
      return { ok: false, problems: ["missing alert name or conditions"] }
    }
    return { ok: true, action: { kind: action.kind, args: { config } } }
  }
  if (action.kind === "test_alert") {
    const alert_id = readString(action.args, "alert_id")
    const run_id = readString(action.args, "run_id")
    if (!alert_id || !run_id) {
      return { ok: false, problems: ["missing alert_id or run_id"] }
    }
    return {
      ok: true,
      action: { kind: action.kind, args: { alert_id, run_id } },
    }
  }
  if (action.kind !== "update_alert" && action.kind !== "delete_alert") {
    return undefined
  }
  const alert_id = readString(action.args, "alert_id")
  const expected_revision = readString(action.args, "expected_revision")
  if (!alert_id || !expected_revision) {
    return { ok: false, problems: ["missing alert identity or revision"] }
  }
  if (action.kind === "delete_alert") {
    return {
      ok: true,
      action: { kind: action.kind, args: { alert_id, expected_revision } },
    }
  }
  const config = readJsonObject(action.args, "config")
  if (!config || Object.keys(config).length === 0) {
    return { ok: false, problems: ["missing alert changes"] }
  }
  return {
    ok: true,
    action: {
      kind: action.kind,
      args: { alert_id, expected_revision, config },
    },
  }
}

const OPERATORS: Record<string, string> = {
  GT: ">",
  GTE: "≥",
  LT: "<",
  LTE: "≤",
  EQ: "=",
  NEQ: "≠",
}
const AGGREGATIONS: Record<string, string> = {
  SINGLE: "Individual value",
  RUN_AVERAGE: "Run average",
  RUN_FRACTION: "Fraction of matching results",
  JOB_SUCCESS: "Run outcome",
  BASELINE_DEVIATION: "Fraction outside baseline",
}

function displayValue(value: unknown): string {
  if (typeof value === "string") {
    return value
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  return value == null ? "" : JSON.stringify(value)
}

function conditionText(condition: JsonObject): string {
  const target =
    condition.aggregation === "SINGLE"
      ? (condition.threshold_string ??
        condition.match_value ??
        condition.threshold_float)
      : condition.threshold_float
  const metric =
    condition.metric_id ?? "All simulations completed (yes = 1, no = 0)"
  const detail = Object.entries(condition)
    .filter(
      ([key, value]) =>
        ![
          "metric_id",
          "aggregation",
          "operator",
          "threshold_float",
          "threshold_string",
        ].includes(key) && value != null,
    )
    .map(
      ([key, value]) => `${key.replaceAll("_", " ")}: ${displayValue(value)}`,
    )
  return `${displayValue(metric)} · ${AGGREGATIONS[displayValue(condition.aggregation)] ?? displayValue(condition.aggregation)} ${OPERATORS[displayValue(condition.operator)] ?? ""} ${displayValue(target ?? "")} ${detail.join(" · ")}`.trim()
}

/** Show every condition and recipient before confirming a replacement array. */
export function summarizeAlertActionArgs(
  action: SofiaProposedAction,
): Array<{ key: string; value: string }> {
  if (action.kind === "test_alert") {
    return [
      { key: "alert", value: String(action.args.alert_id) },
      { key: "run", value: String(action.args.run_id) },
      {
        key: "test",
        value: "Dry run only. No notifications sent or event recorded.",
      },
    ]
  }
  const config = isRecord(action.args.config) ? action.args.config : {}
  return Object.entries(config).flatMap(([key, value]) => {
    if (key === "conditions" && Array.isArray(value)) {
      return [
        { key: "conditions", value: "Complete set of conditions" },
        ...value.filter(isRecord).map((condition, i) => ({
          key: `condition ${i + 1}`,
          value: conditionText(condition as JsonObject),
        })),
      ]
    }
    if (key === "channels" && Array.isArray(value)) {
      if (!value.length) {
        return [{ key: "channels", value: "Remove all notification channels" }]
      }
      return [
        { key: "channels", value: "Complete set of notification channels" },
        ...value.filter(isRecord).map((channel, i) => ({
          key: `channel ${i + 1}`,
          value: `${String(channel.channel_type)}: ${JSON.stringify(channel.config)}`,
        })),
      ]
    }
    if (key === "evaluation_type") {
      return [{ key: "evaluate", value: "When a run completes" }]
    }
    const label = key.replaceAll("_", " ")
    if (Array.isArray(value)) {
      return [
        {
          key: label,
          value: value.length
            ? value.map(String).join(", ")
            : "Clear restriction",
        },
      ]
    }
    return [{ key: label, value: value === "" ? "Clear" : displayValue(value) }]
  })
}
